/* ==========================================================================
   VD Top Stylists LP — single script for the whole page
   Figma: "💊 CRO Tests" › "📜 LP 4: Top Stylists LP" (4370:3382)

   Handles:
     · scroll-reveal for section content (IntersectionObserver)
     · the before/after transformations carousel  (data-vdts-carousel="proof")
     · the customer video reels carousel          (data-vdts-carousel="reels")
     · click-to-play video facades                (data-vdts-play)

   Deferred, dependency-free, and safe to load more than once — each
   section tags itself with a flag so re-initialisation is a no-op
   (Shopify's theme editor re-renders sections in place).
   ========================================================================== */
(function () {
  'use strict';

  // Two sections reference this file; Shopify does not dedupe <script> tags,
  // so guard against a second execution.
  if (window.__vdtsTopStylistsLp) return;
  window.__vdtsTopStylistsLp = true;

  // NB: the hero is deliberately excluded from scroll-reveal — fading the
  // LCP element in would delay Largest Contentful Paint.
  var REVEAL_SELECTORS = [
    '.vdts-endorse__head',
    '.vdts-endorse__card',
    '.vdts-mech__head',
    '.vdts-mech__media',
    '.vdts-mech__info',
    '.vdts-mech__cta',
    '.vdts-timeline .vdts-head',
    '.vdts-timeline__node',
    '.vdts-proof__head',
    '.vdts-proof__carousel',
    '.vdts-reels .vdts-head',
    '.vdts-reels__carousel',
    '.vdts-founder__media',
    '.vdts-founder__card',
    '.vdts-expect__copy',
    '.vdts-faq__inner > *',
    '.vdts-ghdg__banner'
  ].join(',');

  var prefersReducedMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------- reveal */
  var revealObserver = null;

  function getRevealObserver() {
    if (revealObserver || !('IntersectionObserver' in window)) return revealObserver;
    revealObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          revealObserver.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.08 }
    );
    return revealObserver;
  }

  function initReveal(root) {
    document.documentElement.classList.add('vdts-ready');

    var nodes = root.querySelectorAll(REVEAL_SELECTORS);
    var observer = prefersReducedMotion ? null : getRevealObserver();

    Array.prototype.forEach.call(nodes, function (node, index) {
      if (node.hasAttribute('data-vdts-reveal')) return;
      node.setAttribute('data-vdts-reveal', '');

      if (!observer) {
        node.classList.add('is-visible');
        return;
      }
      // Stagger siblings slightly so grids cascade rather than pop as one.
      node.style.transitionDelay = Math.min(index % 4, 3) * 70 + 'ms';
      observer.observe(node);
    });
  }

  /* ------------------------------------------------------------- carousels */
  function slideStep(track) {
    var slides = track.querySelectorAll('[data-vdts-slide]');
    if (slides.length < 2) return slides.length ? slides[0].offsetWidth : 0;
    return Math.abs(slides[1].offsetLeft - slides[0].offsetLeft);
  }

  function initCarousel(carousel) {
    if (carousel.hasAttribute('data-vdts-carousel-ready')) return;
    carousel.setAttribute('data-vdts-carousel-ready', '');

    var viewport = carousel.querySelector('[data-vdts-viewport]');
    var track = carousel.querySelector('[data-vdts-track]');
    var prev = carousel.querySelector('[data-vdts-prev]');
    var next = carousel.querySelector('[data-vdts-next]');
    if (!viewport || !track) return;

    function scrollByStep(direction) {
      var step = slideStep(track);
      if (!step) return;
      viewport.scrollBy({
        left: step * direction,
        behavior: prefersReducedMotion ? 'auto' : 'smooth'
      });
    }

    function syncButtons() {
      if (!prev || !next) return;
      var max = viewport.scrollWidth - viewport.clientWidth;
      var x = viewport.scrollLeft;
      prev.disabled = x <= 1;
      next.disabled = x >= max - 1;
    }

    if (prev) prev.addEventListener('click', function () { scrollByStep(-1); });
    if (next) next.addEventListener('click', function () { scrollByStep(1); });

    viewport.addEventListener('scroll', function () {
      window.clearTimeout(viewport._vdtsScrollTimer);
      viewport._vdtsScrollTimer = window.setTimeout(syncButtons, 80);
    });

    viewport.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        scrollByStep(1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        scrollByStep(-1);
      }
    });

    window.addEventListener('resize', syncButtons, { passive: true });
    syncButtons();
  }

  /* --------------------------------------------------------- video facades */

  /* Stop a playing card. A <video> is paused in place so the visitor can
     resume where they left off; an <iframe> embed exposes no cross-origin
     pause API, so the only way to silence it is to tear the facade back down
     to its poster — coming back means pressing play again. */
  function stopFacade(card) {
    if (!card || !card.classList.contains('is-playing')) return;

    var video = card.querySelector('video');
    if (video) {
      if (!video.paused) video.pause();
      return;
    }

    var frame = card.querySelector('iframe');
    if (frame) {
      frame.parentNode.removeChild(frame);
      card.classList.remove('is-playing');
      card.classList.remove('is-ready');
    }
  }

  /* Pause a reel once it leaves the screen — whether the visitor scrolled on
     to another section or swiped the carousel to another card. Ancestor clip
     rects are folded into the intersection, so the carousel viewport counts
     as "off screen" without a second observer. Below a quarter visible the
     video is no longer really being watched, but it is still audible; that is
     the point at which it should stop. */
  var facadeObserver = null;

  function getFacadeObserver() {
    if (facadeObserver || !('IntersectionObserver' in window)) return facadeObserver;
    facadeObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.intersectionRatio < 0.25) stopFacade(entry.target);
        });
      },
      { threshold: [0, 0.25] }
    );
    return facadeObserver;
  }

  function initVideoFacade(button) {
    if (button.hasAttribute('data-vdts-play-ready')) return;
    button.setAttribute('data-vdts-play-ready', '');

    button.addEventListener('click', function () {
      var card = button.closest('[data-vdts-video-card]');
      if (!card || card.classList.contains('is-playing')) return;

      var template = card.querySelector('[data-vdts-video-source]');
      if (!template) return;

      card.appendChild(template.content.cloneNode(true));
      card.classList.add('is-playing');

      var observer = getFacadeObserver();
      if (observer && !card.hasAttribute('data-vdts-watched')) {
        card.setAttribute('data-vdts-watched', '');
        observer.observe(card);
      }

      var video = card.querySelector('video');

      /* Swap the poster for the player only once there is a frame to swap to.
         `loadeddata` is the earliest guarantee of that (readyState 2); the
         `error` case reveals anyway so a broken source shows the browser's own
         failure state rather than stranding the visitor on a dead poster. */
      var reveal = function () { card.classList.add('is-ready'); };

      if (!video) {
        reveal(); // iframe embeds paint their own poster and player UI
        return;
      }

      if (video.readyState >= 2) {
        reveal();
      } else {
        video.addEventListener('loadeddata', reveal, { once: true });
        video.addEventListener('playing', reveal, { once: true });
        video.addEventListener('error', reveal, { once: true });
      }

      if (typeof video.play === 'function') {
        video.setAttribute('playsinline', '');
        var playing = video.play();
        if (playing && typeof playing.catch === 'function') {
          playing.catch(function () {
            /* Autoplay blocked — the native controls remain available. */
          });
        }
      }
    });
  }

  /* ------------------------------------------- ambient (autoplaying) video */
  var ambientObserver = null;

  function getAmbientObserver() {
    if (ambientObserver || !('IntersectionObserver' in window)) return ambientObserver;
    ambientObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var video = entry.target;
          if (entry.isIntersecting) {
            var playing = video.play();
            if (playing && typeof playing.catch === 'function') {
              playing.catch(function () {
                /* Autoplay refused (e.g. Low Power Mode) — leave the poster up. */
              });
            }
          } else if (!video.paused) {
            video.pause();
          }
        });
      },
      { threshold: 0.15 }
    );
    return ambientObserver;
  }

  function initAmbientVideo(video) {
    if (video.hasAttribute('data-vdts-ambient-ready')) return;
    video.setAttribute('data-vdts-ambient-ready', '');

    // Belt and braces: iOS only honours inline autoplay when both are present.
    video.muted = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');

    if (prefersReducedMotion) {
      video.removeAttribute('autoplay');
      video.pause();
      video.controls = true; // let the visitor start it themselves instead
      return;
    }

    var observer = getAmbientObserver();
    if (observer) observer.observe(video);
  }

  /* ------------------------------------------------ founder image sequence */
  function initFounderSlider(root) {
    if (root.hasAttribute('data-vdts-founder-ready')) return;
    root.setAttribute('data-vdts-founder-ready', '');

    var track = root.querySelector('[data-vdts-founder-track]');
    var toggle = root.querySelector('[data-vdts-founder-toggle]');
    if (!track) return;

    var count = track.children.length;
    if (count < 2) {
      if (toggle) toggle.hidden = true;
      return;
    }

    var interval = parseInt(root.getAttribute('data-interval'), 10) || 4000;
    var wantsAutoplay = root.getAttribute('data-autoplay') !== 'false' && !prefersReducedMotion;
    var index = 0;
    var timer = null;
    var inView = true;

    var slides = Array.prototype.slice.call(track.children);

    function show(i) {
      index = (i + count) % count;
      track.style.setProperty('--vdts-founder-i', index);
      // Marks the live slide so the before/after pair can converge to centre.
      slides.forEach(function (slide, n) {
        slide.classList.toggle('is-active', n === index);
      });
    }

    function stop() {
      window.clearInterval(timer);
      timer = null;
    }

    function start() {
      if (timer || !wantsAutoplay || !inView) return;
      timer = window.setInterval(function () { show(index + 1); }, interval);
    }

    function setPaused(paused) {
      root.classList.toggle('is-paused', paused);
      if (toggle) {
        toggle.setAttribute('aria-label', paused ? 'Play image animation' : 'Pause image animation');
      }
      if (paused) stop(); else start();
    }

    if (toggle) {
      toggle.addEventListener('click', function () {
        wantsAutoplay = !wantsAutoplay;
        setPaused(!wantsAutoplay);
      });
    }

    // Don't burn a timer (or animate) while the section is off-screen.
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        function (entries) {
          inView = entries[0].isIntersecting;
          if (inView) start(); else stop();
        },
        { threshold: 0.2 }
      ).observe(root);
    }

    show(0);
    setPaused(!wantsAutoplay);
  }

  /* ------------------------------------------------------------------ boot */
  function init(root) {
    var scope = root || document;
    if (!scope.querySelector('.vdts-section')) return;

    initReveal(scope);

    Array.prototype.forEach.call(
      scope.querySelectorAll('video.vdts-mech__video'),
      initAmbientVideo
    );
    Array.prototype.forEach.call(
      scope.querySelectorAll('[data-vdts-founder-slider]'),
      initFounderSlider
    );

    Array.prototype.forEach.call(
      scope.querySelectorAll('[data-vdts-carousel]'),
      initCarousel
    );
    Array.prototype.forEach.call(
      scope.querySelectorAll('[data-vdts-play]'),
      initVideoFacade
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  // Shopify theme editor: re-run for the section that was just re-rendered.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
